import {
	type Component,
	isKeyRelease,
	type OverlayHandle,
	type OverlayOptions,
	type Terminal,
	type TUI,
	TuiMainScreen,
} from "@earendil-works/pi-tui";
import { getAgentDir } from "../../config.js";
import { runCallback } from "../../core/callback-activity.ts";
import type { HostCustomUiState, HostCustomUiStateListener } from "../../core/extensions/index.js";
import type { ScrollableWidgetComponent } from "../../core/extensions/ui-types.js";
import type { KeybindingsManager } from "../../core/keybindings.ts";
import { ensurePngTranscoder } from "../../utils/image-convert.ts";
import type { Theme } from "../interactive/theme/theme.js";
import { theme } from "../interactive/theme/theme.js";
import {
	type EngineTerminalControl,
	type InteractiveEngineMessage,
	isJsonValue,
	type JsonValue,
	parseInteractiveEngineCommand,
	type SerializableOverlayOptions,
	serializeInteractiveEngineMessage,
} from "./protocol.ts";

interface CustomUiOptions {
	purpose?: "prompt" | "navigation";
	overlay?: boolean;
	deferInlineCustomUiFocus?: boolean;
	/** The component binds Ctrl+C itself; see ExtensionUIContext.custom options. */
	handlesCtrlC?: boolean;
	/** The component claims internal UI actions such as the jump-to-bottom URL. */
	handlesInternalUiAction?: boolean;
	/** Bound this overlay and reserve transcript scroll extent on the host. */
	reserveTranscriptRows?: boolean;
	overlayOptions?: OverlayOptions | (() => OverlayOptions);
	onHandle?: (handle: OverlayHandle) => void;
	signal?: AbortSignal;
}

interface ActiveComponent {
	component: Component & { dispose?(): void };
	resolve: (value: JsonValue | undefined) => void;
	overlay: boolean;
	purpose?: "prompt" | "navigation";
	terminal: RemoteTerminal;
	tui: TUI;
	widgetKey?: string;
	/**
	 * Overlay visibility as last driven through the remote OverlayHandle.
	 * Hidden components are skipped by the extension-level `requestRender()`
	 * broadcast (#1856) — invalidating a hidden overlay only produced host
	 * render work and terminal writes for frames the user cannot see.
	 */
	hidden?: boolean;
}

class RemoteTerminal implements Terminal {
	columns = 80;
	rows = 24;
	kittyProtocolActive = false;
	private readonly invalidate: () => void;
	private readonly control: ((control: EngineTerminalControl) => void) | undefined;
	private readonly copy: ((requestId: number, text: string) => void) | undefined;
	private nextCopyId = 0;
	private readonly pendingCopies = new Map<
		number,
		{ resolve: () => void; reject: (error: Error) => void; timer: ReturnType<typeof setTimeout> }
	>();
	constructor(
		invalidate: () => void,
		control?: (control: EngineTerminalControl) => void,
		copy?: (requestId: number, text: string) => void,
	) {
		this.invalidate = invalidate;
		this.control = control;
		this.copy = copy;
	}
	copyTextToHost(text: string): Promise<void> {
		if (!this.copy) return Promise.reject(new Error("Host clipboard unavailable"));
		return new Promise((resolve, reject) => {
			const requestId = ++this.nextCopyId;
			const timer = setTimeout(() => {
				this.pendingCopies.delete(requestId);
				reject(new Error("Host clipboard did not respond"));
			}, 15_000);
			this.pendingCopies.set(requestId, { resolve, reject, timer });
			this.copy?.(requestId, text);
		});
	}
	resolveCopy(requestId: number, error?: string): void {
		const pending = this.pendingCopies.get(requestId);
		if (!pending) return;
		clearTimeout(pending.timer);
		this.pendingCopies.delete(requestId);
		if (error) pending.reject(new Error(error));
		else pending.resolve();
	}
	close(): void {
		for (const requestId of this.pendingCopies.keys()) this.resolveCopy(requestId, "Sign-in screen closed");
	}
	start(): void {}
	stop(): void {}
	async drainInput(): Promise<void> {}
	write(): void {
		this.invalidate();
	}
	moveBy(): void {}
	hideCursor(): void {}
	showCursor(): void {}
	clearLine(): void {}
	clearFromCursor(): void {}
	clearScreen(): void {}
	setTitle(): void {}
	setProgress(): void {}
	setProgramStatus(): void {}
	/** Ask the host to toggle host-TTY autowrap (DECAWM) for this component. */
	setAutowrap(enabled: boolean): void {
		this.control?.({ kind: "autowrap", enabled });
	}
}

function serializableOverlayOptions(
	options: CustomUiOptions["overlayOptions"],
): SerializableOverlayOptions | undefined {
	const value = typeof options === "function" ? options() : options;
	if (!value) return undefined;
	const { anchor, col, margin, maxHeight, minWidth, offsetX, offsetY, row, width } = value;
	return { anchor, col, margin, maxHeight, minWidth, offsetX, offsetY, row, width };
}

function jsonResult(value: object | boolean | null | number | string | undefined): JsonValue | undefined {
	if (value === undefined) return undefined;
	const encoded = JSON.stringify(value);
	if (encoded === undefined) return undefined;
	const decoded = JSON.parse(encoded) as JsonValue;
	if (!isJsonValue(decoded)) throw new Error("Custom UI result is not JSON-safe");
	return decoded;
}

export class EngineCustomUiService {
	private readonly widgetIds = new Map<string, string>();
	private readonly active = new Map<string, ActiveComponent>();
	private nextId = 0;
	private readonly write: (line: string) => void;
	private readonly keybindings: KeybindingsManager;
	private readonly stateListeners = new Set<HostCustomUiStateListener>();
	private readonly widgetReleaseListeners = new Map<string, Set<() => void>>();

	setWidget(
		key: string,
		factory: ((tui: TUI, theme: Theme) => Component & { dispose?(): void }) | undefined,
		placement?: "aboveEditor" | "belowEditor",
		scroll?: { maxHeight: number; maxHeightFraction?: number },
	): void {
		const previous = this.widgetIds.get(key);
		if (previous) this.disposeComponent(previous, false, false);
		if (!factory) {
			this.notifyWidgetRelease(key);
			return;
		}
		const componentId = `remote_widget_${++this.nextId}`;
		this.widgetIds.set(key, componentId);
		const terminal = new RemoteTerminal(() => this.send({ type: "engine_custom_invalidate", componentId }));
		const tui = new TuiMainScreen(terminal, undefined, getAgentDir());
		ensurePngTranscoder(() => {
			tui.invalidate();
			tui.requestRender();
		});
		void runCallback({ kind: "renderer", name: `widget:${key}` }, () => factory(tui, theme))
			.then((component) => {
				if (this.widgetIds.get(key) !== componentId) {
					component.dispose?.();
					return;
				}
				tui.addChild(component);
				this.active.set(componentId, {
					component,
					resolve: () => {},
					overlay: false,
					terminal,
					tui,
					widgetKey: key,
				});
				this.send({
					type: "engine_custom_open",
					componentId,
					overlay: false,
					widgetKey: key,
					widgetPlacement: placement,
					...(scroll ? { widgetScroll: scroll } : {}),
				});
			})
			.catch((error: Error) => {
				if (this.widgetIds.get(key) === componentId) this.widgetIds.delete(key);
				this.send({
					type: "engine_custom_frame",
					componentId,
					requestId: 0,
					lines: [`Widget ${key} failed: ${error.message}`],
				});
			});
	}
	constructor(write: (line: string) => void, keybindings: KeybindingsManager) {
		this.write = write;
		this.keybindings = keybindings;
	}

	async custom<T>(
		factory: (
			tui: TUI,
			theme: Theme,
			keybindings: KeybindingsManager,
			done: (result: T) => void,
		) => (Component & { dispose?(): void }) | Promise<Component & { dispose?(): void }>,
		options?: CustomUiOptions,
	): Promise<T> {
		const componentId = `remote_component_${++this.nextId}`;
		let resolveCompletion!: (value: T) => void;
		const completion = new Promise<T>((resolve) => {
			resolveCompletion = resolve;
		});
		let opened = false;
		let pendingDone: T | undefined;
		let doneCalled = false;
		const done = (result: T): void => {
			if (doneCalled) return;
			doneCalled = true;
			pendingDone = result;
			resolveCompletion(result);
			if (opened) this.sendDone(componentId, result);
		};
		const terminal = new RemoteTerminal(
			() => this.send({ type: "engine_custom_invalidate", componentId }),
			(control) => this.send({ type: "engine_custom_terminal", componentId, control }),
			(requestId, text) => this.send({ type: "engine_custom_copy", componentId, requestId, text }),
		);
		const tui = new TuiMainScreen(terminal, undefined, getAgentDir());
		ensurePngTranscoder(() => {
			tui.invalidate();
			tui.requestRender();
		});
		const component = await factory(tui, theme, this.keybindings, done);
		tui.addChild(component);
		tui.setFocus(component);
		const record: ActiveComponent = {
			component,
			// Cancellation must settle through the same guarded host completion as done().
			resolve: (value) => done(value as T),
			terminal,
			tui,
			overlay: options?.overlay === true,
			purpose: options?.purpose,
		};
		this.active.set(componentId, record);
		this.notifyState();
		opened = true;
		this.send({
			type: "engine_custom_open",
			componentId,
			overlay: options?.overlay === true,
			purpose: options?.purpose,
			deferInlineCustomUiFocus: options?.deferInlineCustomUiFocus,
			handlesCtrlC: options?.handlesCtrlC,
			handlesInternalUiAction: options?.handlesInternalUiAction,
			reserveTranscriptRows: options?.reserveTranscriptRows,
			overlayOptions: serializableOverlayOptions(options?.overlayOptions),
		});
		if (options?.onHandle) options.onHandle(this.remoteHandle(componentId));
		if (doneCalled) this.sendDone(componentId, pendingDone as T);
		const onAbort = () => this.disposeComponent(componentId, true);
		options?.signal?.addEventListener("abort", onAbort, { once: true });
		try {
			return await completion;
		} finally {
			options?.signal?.removeEventListener("abort", onAbort);
			this.disposeComponent(componentId, false);
		}
	}
	getHostCustomUiState(): HostCustomUiState {
		const inline = [...this.active.values()].filter((record) => !record.overlay && !record.widgetKey);
		const navigation = inline.filter((record) => record.purpose === "navigation").length;
		return {
			blockingInlineCustomUiDepth: inline.length,
			blockingInlineCustomUiActive: inline.length > 0,
			...(navigation > 0 ? { blockingInlineCustomUiNeedsInput: inline.length > navigation } : {}),
		};
	}

	onHostCustomUiStateChange(listener: HostCustomUiStateListener): () => void {
		this.stateListeners.add(listener);
		return () => this.stateListeners.delete(listener);
	}
	onWidgetRelease(key: string, listener: () => void): () => void {
		const listeners = this.widgetReleaseListeners.get(key) ?? new Set<() => void>();
		listeners.add(listener);
		this.widgetReleaseListeners.set(key, listeners);
		return () => {
			listeners.delete(listener);
			if (listeners.size === 0) this.widgetReleaseListeners.delete(key);
		};
	}

	focusHostInlineCustomUi(): boolean {
		return this.getHostCustomUiState().blockingInlineCustomUiActive;
	}

	requestRender(): void {
		for (const [componentId, record] of this.active) {
			if (record.hidden === true) continue;
			this.send({ type: "engine_custom_invalidate", componentId });
		}
	}

	handleLine(line: string): boolean {
		const command = parseInteractiveEngineCommand(line);
		if (!command) return false;
		if (!command.type.startsWith("engine_custom_")) return false;
		const record = this.active.get(command.componentId);
		if (!record) {
			if (command.type === "engine_custom_input")
				this.send({
					type: "engine_custom_input_result",
					componentId: command.componentId,
					requestId: command.requestId,
					handled: false,
				});
			return true;
		}
		switch (command.type) {
			case "engine_custom_scroll":
				(record.component as ScrollableWidgetComponent).onScroll?.(command.state);
				break;
			case "engine_custom_render":
				record.terminal.columns = Math.max(1, command.width);
				record.terminal.rows = Math.max(1, command.rows);
				void runCallback({ kind: "renderer", name: command.componentId }, () =>
					record.component.render(record.terminal.columns),
				)
					.then((lines) => {
						const scrollRequest = (record.component as ScrollableWidgetComponent).getScrollRequest?.();
						this.send({
							type: "engine_custom_frame",
							componentId: command.componentId,
							requestId: command.requestId,
							lines,
							...(scrollRequest ? { scrollRequest } : {}),
						});
					})
					.catch((error: Error) =>
						this.send({
							type: "engine_custom_frame",
							componentId: command.componentId,
							requestId: command.requestId,
							lines: [`Remote component render failed: ${error.message}`],
						}),
					);
				break;
			case "engine_custom_input":
				void runCallback({ kind: "renderer", name: command.componentId }, () => {
					if (isKeyRelease(command.data) && record.component.wantsKeyRelease !== true) return false;
					const handleInput = record.component.handleInput as ((data: string) => boolean | undefined) | undefined;
					return handleInput?.call(record.component, command.data) === true;
				})
					.then((handled) =>
						this.send({
							type: "engine_custom_input_result",
							componentId: command.componentId,
							requestId: command.requestId,
							handled,
						}),
					)
					.catch(() =>
						this.send({
							type: "engine_custom_input_result",
							componentId: command.componentId,
							requestId: command.requestId,
							handled: false,
						}),
					);
				break;
			case "engine_custom_copy_result":
				record.terminal.resolveCopy(command.requestId, command.error);
				break;
			case "engine_custom_dispose":
				this.disposeComponent(command.componentId, true);
				break;
		}
		return true;
	}

	dispose(): void {
		for (const componentId of [...this.active.keys()]) this.disposeComponent(componentId, true, false);
	}
	private disposeComponent(componentId: string, resolve: boolean, notifyWidgetRelease = true): void {
		const record = this.active.get(componentId);
		if (!record) return;
		this.active.delete(componentId);
		record.terminal.close();
		// Claim cancellation before user disposal code can call done() reentrantly.
		if (resolve) record.resolve(undefined);
		record.component.dispose?.();
		record.tui.stop();
		if (record.widgetKey) {
			if (this.widgetIds.get(record.widgetKey) === componentId) this.widgetIds.delete(record.widgetKey);
			this.send({ type: "engine_custom_close", componentId });
			if (notifyWidgetRelease) this.notifyWidgetRelease(record.widgetKey);
		}
		this.notifyState();
	}
	private notifyWidgetRelease(key: string): void {
		for (const listener of this.widgetReleaseListeners.get(key) ?? []) {
			try {
				listener();
			} catch {
				// A release observer must not break engine UI teardown.
			}
		}
	}
	private notifyState(): void {
		const state = this.getHostCustomUiState();
		for (const listener of this.stateListeners) listener(state);
	}

	private remoteHandle(componentId: string): OverlayHandle {
		let focused = true;
		const setHiddenFlag = (value: boolean): void => {
			const record = this.active.get(componentId);
			if (record) record.hidden = value;
		};
		return {
			hide: () => {
				setHiddenFlag(true);
				this.send({ type: "engine_custom_control", componentId, action: "hide" });
			},
			setHidden: (value) => {
				setHiddenFlag(value);
				this.send({ type: "engine_custom_control", componentId, action: value ? "hide" : "show" });
			},
			isHidden: () => this.active.get(componentId)?.hidden === true,
			focus: () => {
				focused = true;
				this.send({ type: "engine_custom_control", componentId, action: "focus" });
			},
			unfocus: () => {
				focused = false;
				this.send({ type: "engine_custom_control", componentId, action: "unfocus" });
			},
			isFocused: () => focused,
			getBounds: () => undefined,
		};
	}

	private sendDone<T>(componentId: string, result: T): void {
		this.send({
			type: "engine_custom_done",
			componentId,
			result: jsonResult(result as object | boolean | null | number | string | undefined),
		});
	}

	private send(message: InteractiveEngineMessage): void {
		this.write(serializeInteractiveEngineMessage(message));
	}
}
